import React, { useState, useEffect } from "react";
import { Row, Col, Alert, Spinner } from "reactstrap";
import Widget from "../../../components/Widget/Widget";
import KPICard from "./KPICard";
import StockChart from "./StockChart";
import PermintaanTable from "./PermintaanTable";
import ExpiredAlert from "./ExpiredAlert";
import RekapanPuskesmasTable from "./RekapanPuskesmasTable";
import RekapanPuskesmasChart from "./RekapanPuskesmasChart";
import { STOCK_GUDANG, API_TIMEOUT, DASHBOARD } from "../../../utils";
import axios from "axios";

/**
 * Dashboard untuk User Gudang
 * Menampilkan informasi stok gudang, permintaan, dan distribusi
 */
export default function GudangDashboard({ currentUser }) {
  const [loading, setLoading] = useState(true);
  const [stockData, setStockData] = useState([]);
  const [rekapanPuskesmas, setRekapanPuskesmas] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [permintaanTerbaru, setPermintaanTerbaru] = useState([]);
  const [ringkasanPermintaan, setRingkasanPermintaan] = useState({
    total_permintaan: 0,
    total_obat: 0,
    total_pending: 0,
    total_disetujui: 0,
    total_ditolak: 0,
  });
  const [summaryData, setSummaryData] = useState({
    totalStok: 0,
    totalObat: 0,
    stokReady: 0,
    stokExpired: 0,
    permintaanPending: 0,
    distribusiBulanIni: 0,
    jumlahPuskesmas: 0,
  });
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const idTujuan = currentUser?.idLokasi;

      const [
        stockResponse,
        rekapanResponse,
        trendResponse,
        permintaanResponse,
        ringkasanResponse,
      ] = await Promise.all([
        axios.get(STOCK_GUDANG, { timeout: API_TIMEOUT }),
        axios.get(`${DASHBOARD}ambil/rekap/${selectedYear}`, {
          timeout: API_TIMEOUT,
        }),
        axios.get(`${DASHBOARD}gudang/trend/${selectedYear}`, {
          timeout: API_TIMEOUT,
        }),
        idTujuan
          ? axios.get(`${DASHBOARD}gudang/permintaan-terbaru/${idTujuan}`, {
              timeout: API_TIMEOUT,
            })
          : Promise.resolve({ data: { success: 1, data: [] } }),
        idTujuan
          ? axios.get(`${DASHBOARD}gudang/permintaan-ringkasan/${idTujuan}`, {
              timeout: API_TIMEOUT,
            })
          : Promise.resolve({ data: { success: 1, data: null } }),
      ]);

      if (stockResponse.data.success) {
        const data = stockResponse.data.data || [];
        setStockData(data);

        const totalStok = data.reduce((sum, item) => sum + (item.stok || 0), 0);
        const totalObat = data.length;
        const stokReady = data.reduce(
          (sum, item) => sum + (item.stok_ready || 0),
          0
        );
        const stokExpired = data.reduce(
          (sum, item) => sum + (item.stok_expired || 0),
          0
        );

        let distribusiBulanIni = 0;
        let jumlahPuskesmas = 0;

        if (rekapanResponse.data.success) {
          const rekapanData = rekapanResponse.data.data || [];
          setRekapanPuskesmas(rekapanData);
          jumlahPuskesmas = rekapanData.length;
          distribusiBulanIni = rekapanData.reduce(
            (sum, item) => sum + (item.total_keluar || 0),
            0
          );
        } else {
          setRekapanPuskesmas([]);
        }

        if (trendResponse.data.success) {
          setTrendData(trendResponse.data.data || []);
        } else {
          setTrendData([]);
        }

        if (permintaanResponse.data.success) {
          const permintaanData = (permintaanResponse.data.data || []).map(
            (item) => ({
              id: item.id,
              noFaktur: item.noFaktur,
              tanggal: item.tanggal,
              status:
                item.status === 0
                  ? "pending"
                  : item.status === 1
                  ? "disetujui"
                  : item.status === 2
                  ? "ditolak"
                  : "diproses",
              jumlahItem: item.jumlah_item,
              lokasi_asal: item.lokasi_asal,
              total_jumlah: item.total_jumlah,
            })
          );
          setPermintaanTerbaru(permintaanData);
        } else {
          setPermintaanTerbaru([]);
        }

        if (ringkasanResponse.data.success) {
          const ringkasan = ringkasanResponse.data.data || {};
          setRingkasanPermintaan({
            total_permintaan: ringkasan.total_permintaan || 0,
            total_obat: ringkasan.total_obat || 0,
            total_pending: ringkasan.total_pending || 0,
            total_disetujui: ringkasan.total_disetujui || 0,
            total_ditolak: ringkasan.total_ditolak || 0,
          });
        } else {
          setRingkasanPermintaan({
            total_permintaan: 0,
            total_obat: 0,
            total_pending: 0,
            total_disetujui: 0,
            total_ditolak: 0,
          });
        }

        setSummaryData({
          totalStok,
          totalObat,
          stokReady,
          stokExpired,
          permintaanPending:
            ringkasanResponse.data.success && ringkasanResponse.data.data
              ? ringkasanResponse.data.data.total_pending || 0
              : 0,
          distribusiBulanIni,
          jumlahPuskesmas,
        });
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner color="primary" />
        <p className="mt-3">Memuat data dashboard...</p>
      </div>
    );
  }

  return (
    <div>
      <Row className="mb-4">
        <Col xs={12}>
          <h2 className="page-title">
            Dashboard <span className="fw-semi-bold">Gudang</span>
          </h2>
        </Col>
      </Row>

      {/* KPI Cards */}
      <Row className="gx-4 gy-4 mb-4">
        <Col xs={12} sm={6} lg={3}>
          <KPICard
            title="Total Stok"
            value={summaryData.totalStok}
            subtitle={`${summaryData.totalObat} jenis obat`}
            icon="eva eva-package-outline"
            color="primary"
            format="number"
          />
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <KPICard
            title="Stok Ready"
            value={summaryData.stokReady}
            subtitle={`${
              summaryData.totalStok > 0
                ? (
                    (summaryData.stokReady / summaryData.totalStok) *
                    100
                  ).toFixed(1)
                : 0
            }% dari total`}
            icon="eva eva-checkmark-circle-2-outline"
            color="success"
            format="number"
          />
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <KPICard
            title="Stok Expired"
            value={summaryData.stokExpired}
            subtitle={`${
              summaryData.totalStok > 0
                ? (
                    (summaryData.stokExpired / summaryData.totalStok) *
                    100
                  ).toFixed(1)
                : 0
            }% dari total`}
            icon="eva eva-alert-circle-outline"
            color="danger"
            format="number"
          />
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <KPICard
            title="Jumlah Puskesmas"
            value={summaryData.jumlahPuskesmas}
            subtitle="Total puskesmas aktif"
            icon="eva eva-home-outline"
            color="info"
            format="number"
          />
        </Col>
      </Row>

      {/* Rekapan per Puskesmas */}
      <Row className="gx-4 gy-4 mb-4">
        <Col xs={12}>
          <Widget containerClass="h-100 shadow-sm border-0">
            <h5 className="mb-3">
              Rekapan Obat per Puskesmas - Tahun {selectedYear}
            </h5>
            <RekapanPuskesmasTable
              data={rekapanPuskesmas}
              selectedYear={selectedYear}
            />
          </Widget>
        </Col>
      </Row>

      {/* Charts */}
      <Row className="gx-4 gy-4 mb-4">
        <Col xs={12}>
          <Widget containerClass="h-100 shadow-sm border-0">
            <h5 className="mb-3">Grafik Rekapan per Puskesmas</h5>
            <RekapanPuskesmasChart data={rekapanPuskesmas} />
          </Widget>
        </Col>
      </Row>

      {/* Charts Trend Stok */}
      <Row className="gx-4 gy-4 mb-4">
        <Col xs={12} lg={6}>
          <Widget containerClass="h-100 shadow-sm border-0">
            <h5 className="mb-3">Trend Stok (6 Bulan Terakhir)</h5>
            <StockChart data={trendData} />
          </Widget>
        </Col>
        <Col xs={12} lg={6}>
          <Widget containerClass="h-100 shadow-sm border-0">
            <h5 className="mb-3">Obat Expired / Mendekati Expired</h5>
            <ExpiredAlert data={stockData} />
          </Widget>
        </Col>
      </Row>

      {/* Tables */}
      <Row className="gx-4 gy-4 mb-4">
        <Col xs={12} lg={6}>
          <Widget
            containerClass="h-100 shadow-sm border-0"
            className="d-flex flex-column"
          >
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Permintaan Terbaru</h5>
              <span className="text-muted small">
                Total: {ringkasanPermintaan.total_permintaan}
              </span>
            </div>
            <div className="flex-grow-1">
              <PermintaanTable
                data={permintaanTerbaru}
                loading={loading}
                emptyMessage="Belum ada permintaan terbaru"
              />
            </div>
            <div className="pt-3 mt-2 border-top text-muted small">
              <div className="d-flex justify-content-between">
                <span>Pending</span>
                <span className="fw-semibold">
                  {ringkasanPermintaan.total_pending}
                </span>
              </div>
              <div className="d-flex justify-content-between">
                <span>Disetujui</span>
                <span className="fw-semibold">
                  {ringkasanPermintaan.total_disetujui}
                </span>
              </div>
              <div className="d-flex justify-content-between">
                <span>Ditolak</span>
                <span className="fw-semibold">
                  {ringkasanPermintaan.total_ditolak}
                </span>
              </div>
            </div>
          </Widget>
        </Col>
        <Col xs={12} lg={6}>
          <Widget containerClass="h-100 shadow-sm border-0">
            <h5 className="mb-3">Stok Gudang</h5>
            <p className="text-muted">
              Total stok gudang: {summaryData.totalStok} item dari{" "}
              {summaryData.totalObat} jenis obat
            </p>
            <p className="text-muted">
              Stok ready: {summaryData.stokReady} item (
              {summaryData.totalStok > 0
                ? (
                    (summaryData.stokReady / summaryData.totalStok) *
                    100
                  ).toFixed(1)
                : 0}
              %)
            </p>
            <p className="text-muted">
              Stok expired: {summaryData.stokExpired} item (
              {summaryData.totalStok > 0
                ? (
                    (summaryData.stokExpired / summaryData.totalStok) *
                    100
                  ).toFixed(1)
                : 0}
              %)
            </p>
          </Widget>
        </Col>
      </Row>

      {/* Stok Kritis Alert */}
      {summaryData.stokExpired > 0 && (
        <Alert color="warning" className="mb-4">
          <strong>Peringatan!</strong> Terdapat {summaryData.stokExpired} item
          obat yang telah expired. Segera lakukan penanganan.
        </Alert>
      )}
    </div>
  );
}
